import { components } from "@/api-schema";
import { CasaviFilterElement, CasaviFilterType } from "@/api-schema-enums";
import i18next from "i18next";
import { z } from "zod";

type CasaviContactBlacklist = components["schemas"]["CasaviContactBlacklist"];
export type { CasaviContactBlacklist };

/**
 * Validates if a regex pattern is valid
 */
export function validateRegexPattern(value?: string): boolean {
  if (!value || value.trim() === "") {
    return true; // Empty values are allowed
  }
  try {
    new RegExp(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Shared Zod schema for casavi contact blacklist forms
 */
export const CasaviContactBlacklistFormSchema = z
  .object({
    element: z.enum(CasaviFilterElement),
    type: z.enum(CasaviFilterType),
    caseSensitive: z.boolean(),
    value: z.string().min(1, i18next.t("casavi:ValueIsRequired")),
  })
  .refine(
    (data) => {
      if (data.type === CasaviFilterType.Regex) {
        return validateRegexPattern(data.value);
      }
      return true;
    },
    {
      message: i18next.t("common:InvalidRegexPattern"),
      path: ["value"],
    },
  );

export type CasaviContactBlacklistFormSchemaType = z.infer<typeof CasaviContactBlacklistFormSchema>;

/**
 * Validates casavi contact blacklist for duplicates (for create form)
 */
export function validateCasaviContactBlacklistDuplicate(
  contactBlacklists: CasaviContactBlacklist[] | undefined,
  values?: CasaviContactBlacklistFormSchemaType,
): { hasErrors: boolean; errors: string[] } {
  if (!contactBlacklists || !Array.isArray(contactBlacklists) || !values) {
    return { hasErrors: false, errors: [] };
  }

  const errors: string[] = [];

  const duplicate = contactBlacklists.find(
    (item: CasaviContactBlacklist) =>
      item.element === values.element &&
      item.type === values.type &&
      item.caseSensitive === values.caseSensitive &&
      item.value === values.value,
  );

  if (duplicate) {
    errors.push(i18next.t("casavi:ContactBlacklistEntryWithValuesExists"));
  }

  return { hasErrors: errors.length > 0, errors };
}

/**
 * Validates casavi contact blacklist for duplicates (for update form, excluding current entry)
 */
export function validateCasaviContactBlacklistUpdateDuplicate(
  contactBlacklists: CasaviContactBlacklist[] | undefined,
  currentUuid: string | undefined,
  values?: CasaviContactBlacklistFormSchemaType,
): { hasErrors: boolean; errors: string[] } {
  if (!contactBlacklists || !Array.isArray(contactBlacklists) || !values) {
    return { hasErrors: false, errors: [] };
  }

  const errors: string[] = [];

  const duplicate = contactBlacklists.find(
    (item: CasaviContactBlacklist) =>
      item.uuid !== currentUuid &&
      item.element === values.element &&
      item.type === values.type &&
      item.caseSensitive === values.caseSensitive &&
      item.value === values.value,
  );

  if (duplicate) {
    errors.push(i18next.t("casavi:ContactBlacklistOtherEntryWithValuesExists"));
  }

  return { hasErrors: errors.length > 0, errors };
}
